import React, { useContext } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import { AssociateCardSummary } from '../../../../../Types';
import { NO_DATA_LABEL, coachingStatusLabels } from '../../Util/team';
import moment from 'moment';
import TrendTextIndicator from '../TrendTextIndicator';
import { FormattedMessage, useIntl } from 'react-intl';
import { getMetricStringIdAndDefaultMessage } from '../../../../../PerformancePortal/Team/Table/LighthouseMetrics/helper';
import { renderUrl, urls } from '../../../../../Util';
import {
  ARCFilters,
  RELATION_TO_USER,
  SIDE_MENU_PATH,
} from '../../Types/types';
import { StateContext } from '../../../../../GlobalStateStore';
import { formatGoalToString } from '../../../../Goals/helper';
import { formatMetric } from '../../../Table/TimeTrends/helper';
import { useNavigate } from 'react-router';
import {
  Feature,
  isFeatureEnabled,
} from '../../../../../config/FeaturesConfig';
import { MetricsOption } from '@amzn/aet-galactus-kit';

interface RichCardContentProps {
  cardContent: AssociateCardSummary;
  sideMenuSource: SIDE_MENU_PATH;
  isBottomQuartile: boolean;
  BottomQuartileRoutingInfo: string;
  currentVertical: string;
}

const RichCardContent: React.FC<RichCardContentProps> = ({
  cardContent,
  sideMenuSource,
  isBottomQuartile,
  BottomQuartileRoutingInfo,
  currentVertical,
}: RichCardContentProps): React.ReactElement => {
  const intl = useIntl();

  const navigate = useNavigate();

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUsername, userID } = inspireOptions.data;

  const sharedProfileUser =
    cardContent.relationToUser === RELATION_TO_USER.COACHEE;

  const enableHighSilence = isFeatureEnabled(Feature.HIGH_SILENCE_RATE);

  const noDataText = (key: string) => {
    if (key === ARCFilters.PERFORMANCE) {
      return (
        <Row widths='fill'>
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_arc_no_summary'
              defaultMessage='No summary available'
            />
          </Text>
        </Row>
      );
    } else if (key === ARCFilters.OPPORTUNITIES) {
      return (
        <Row widths='fill'>
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_arc_no_opportunities'
              defaultMessage='No current opportunities.'
            />
          </Text>
        </Row>
      );
    } else if (key === ARCFilters.KUDOS) {
      return (
        <Row widths='fill'>
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_arc_no_kudos'
              defaultMessage='No kudos at this time.'
            />
          </Text>
        </Row>
      );
    } else if (key === ARCFilters.GOALS) {
      return (
        <Row widths='fill'>
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_arc_no_open_goals'
              defaultMessage='No active goals'
            />
          </Text>
        </Row>
      );
    } else if (key === ARCFilters.COACHINGS) {
      return (
        <Row widths='fill'>
          <Text type='b300'>
            <FormattedMessage
              id='cs_perfportal_arc_no_recent_coachings'
              defaultMessage='No recent coachings'
            />
          </Text>
        </Row>
      );
    }
  };

  return (
    <Column alignmentVertical='justify' height='100%'>
      {cardContent && cardContent.coachings && (
        <>
          <Row wrap='down' spacing='none' spacingInset='none'>
            <Row width={'100%'} spacingInset='none none 300'>
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_coachings'
                  defaultMessage='Coachings'
                />
              </Text>
            </Row>
            <Column spacing='none' spacingInset='none' width={'100%'}>
              {cardContent.coachings.lastCoachingStatus === null &&
                noDataText(ARCFilters.COACHINGS)}
              <Row widths='fill' spacingInset='none none 300'>
                <Row>
                  <Text type='b300' alignment='start'>
                    <FormattedMessage
                      id='cs_perfportal_arc_coaching_status'
                      defaultMessage='Coaching Status'
                    />
                  </Text>
                </Row>
                <Row alignmentHorizontal='end'>
                  <Text type='b300' alignment='end'>
                    {cardContent.coachings.lastCoachingStatus !== undefined
                      ? coachingStatusLabels(
                          cardContent.coachings.lastCoachingStatus
                        )
                      : '--'}
                  </Text>
                </Row>
              </Row>
              <Row widths='fill'>
                <Row>
                  <Text type='b300' alignment='start'>
                    <FormattedMessage
                      id='cs_perfportal_arc_last_coach_date'
                      defaultMessage='Last Coach Date'
                    />
                  </Text>
                </Row>
                <Row alignmentHorizontal='end'>
                  <Text type='b300' alignment='end'>
                    {moment.utc(cardContent.coachings.lastCoachedDate).isValid()
                      ? moment
                          .utc(cardContent.coachings.lastCoachedDate)
                          .format('MMM. D, YYYY')
                          .toLocaleString()
                      : '--'}
                  </Text>
                </Row>
              </Row>
            </Column>
          </Row>
          <Button
            type='tertiary'
            onClick={navigate}
            href={renderUrl(`${urls.coachingNotes}/${cardContent.userID}`)}
            data-csa-c-type='button'
            data-csa-c-slot-id={`arc-card-${sideMenuSource}-open-coaching-notes-button-slot`}
            data-csa-c-content-id={`arc-card-${sideMenuSource}-open-coaching-notes-for-${cardContent.userID}`}
            data-csa-c-profile-id={`${cardContent.userID}`}
            data-csa-c-tag1='Team'
            data-csa-c-tag2='Associates'
            data-csa-c-tag3='Card'
            data-csa-c-tag4='Coaching'
            data-csa-c-username={requesterUsername}
            data-csa-c-is-tm={isTm}
          >
            <FormattedMessage
              id='cs_perfportal_arc_go_to_coaching_notes'
              defaultMessage='Go to Coaching Notes'
            />
          </Button>
        </>
      )}

      {cardContent && cardContent.opportunities && (
        <>
          <Row wrap='down' spacingInset='none' spacing='none'>
            <Row width='100%' spacingInset='none none 300'>
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_opportunities'
                  defaultMessage='Opportunities'
                />
              </Text>
            </Row>
            <Column spacing='none' spacingInset='none' width={'100%'}>
              {!cardContent.opportunities !== undefined && (
                <Column spacing='none' spacingInset='none'>
                  {cardContent.opportunities.labelAndCount.length === 0 &&
                    noDataText(ARCFilters.OPPORTUNITIES)}
                  {cardContent.opportunities.labelAndCount.map(opportunity => (
                    <Row
                      widths={['60%', '40%']}
                      width='100%'
                      alignmentVertical='top'
                      spacingInset='none none 300'
                      key={opportunity.category}
                    >
                      <Row alignmentHorizontal='start'>
                        <Text type='b300' alignment='start'>
                          {opportunity.category}
                        </Text>
                      </Row>
                      <Row alignmentHorizontal='end'>
                        <Text type='b300' alignment='end'>
                          <FormattedMessage
                            id='cs_perfportal_arc_opportunity_count'
                            defaultMessage='{categoryCount} Counts'
                            values={{ categoryCount: opportunity.count }}
                          />
                        </Text>
                      </Row>
                    </Row>
                  ))}
                </Column>
              )}
            </Column>
          </Row>
          <Button
            type='tertiary'
            onClick={navigate}
            href={renderUrl(
              `${urls.coachingNotes}/${cardContent.userID}#nav-recommended`
            )}
            data-csa-c-type='button'
            data-csa-c-slot-id={`arc-card-${sideMenuSource}-opportunity-view-all-recommendations-button-slot`}
            data-csa-c-content-id={`arc-card-${sideMenuSource}-opportunity-view-all-recommendations-for-${cardContent.userID}`}
            data-csa-c-profile-id={`${cardContent.userID}`}
            data-csa-c-tag1='Team'
            data-csa-c-tag2='Associates'
            data-csa-c-tag3='Card'
            data-csa-c-tag4='Opportunities'
            data-cy={`arc-card-${sideMenuSource}-opportunity-view-all-recommendations-for-${cardContent.userID}`}
          >
            <FormattedMessage
              id='cs_perfportal_arc_go_to_recommendation_button'
              defaultMessage='View All Recommendations'
            />
          </Button>
        </>
      )}

      {cardContent && cardContent.goals && (
        <>
          <Row wrap='down' spacing='none' spacingInset='none'>
            <Row width='100%' spacingInset='none none 300'>
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_arc_active_goals_heading'
                  defaultMessage='Active Goals'
                />
              </Text>
            </Row>
            <Column spacing='none none 300'>
              {cardContent.goals !== undefined && (
                <>
                  {cardContent.goals.length === 0 &&
                    noDataText(ARCFilters.GOALS)}
                  {cardContent.goals?.slice(0, 3).map(goal => (
                    <Row widths='fill' spacingInset='none none 300'>
                      <Text type='b300'>{formatGoalToString(goal, intl)}</Text>
                    </Row>
                  ))}
                </>
              )}
            </Column>
          </Row>

          {!sharedProfileUser && (
            <Button
              type='tertiary'
              onClick={navigate}
              href={renderUrl(
                `${urls.coachingNotes}/${cardContent.userID}#nav-goals`
              )}
              data-csa-c-type='button'
              data-csa-c-slot-id={`arc-card-${sideMenuSource}-goals-view-all-goals-button-slot`}
              data-csa-c-content-id={`arc-card-${sideMenuSource}-goals-view-all-goals-for-${cardContent.userID}`}
              data-csa-c-profile-id={`${cardContent.userID}`}
              data-csa-c-tag1='Team'
              data-csa-c-tag2='Associates'
              data-csa-c-tag3='Card'
              data-csa-c-tag4='Goals'
              data-cy={`arc-card-${sideMenuSource}-goals-view-all-goals-for-${cardContent.userID}`}
            >
              <FormattedMessage
                id='cs_perfportal_arc_go_to_goals_button'
                defaultMessage='View All Goals'
              />
            </Button>
          )}
        </>
      )}
      {cardContent && cardContent.performance && (
        <>
          <Row wrap='down' spacingInset='none' spacing='none'>
            <Row width={'100%'} spacingInset='none none 300'>
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_performance'
                  defaultMessage='Performance'
                />
              </Text>
            </Row>
            <Column spacing='none none 300' width='100%'>
              {cardContent.performance !== undefined && (
                <>
                  {cardContent.performance.trendSummary.length === 0 &&
                    noDataText(ARCFilters.PERFORMANCE)}
                  {cardContent.performance.trendSummary
                    .sort((a, b) => {
                      if (a && b) return a.metric.localeCompare(b.metric);
                      if (!a) return 1;
                      if (!b) return -1;
                      return 0;
                    })
                    .map(trendData => {
                      if (
                        !enableHighSilence &&
                        trendData?.metric === MetricsOption.HIGH_SILENCE_RATE
                      )
                        return <></>;
                      return (
                        <Row
                          width='100%'
                          widths='fill'
                          alignmentVertical='top'
                          key={trendData?.metric}
                        >
                          <Row width={150}>
                            <Text type='b300' alignment='start'>
                              {trendData?.metric && (
                                <FormattedMessage
                                  {...getMetricStringIdAndDefaultMessage(
                                    trendData?.metric
                                  )}
                                />
                              )}
                            </Text>
                          </Row>
                          <Row
                            alignmentVertical='top'
                            alignmentHorizontal='justify'
                          >
                            <Text type='b300'>
                              {trendData?.endValue &&
                                formatMetric(trendData?.endValue)}
                            </Text>
                            <TrendTextIndicator
                              metric={trendData?.metric}
                              label={trendData?.delta ?? NO_DATA_LABEL}
                              trend={trendData?.trendIndicator ?? 'neutral'}
                              showLongTrendDescription={false}
                            />
                          </Row>
                        </Row>
                      );
                    })}
                </>
              )}
            </Column>
          </Row>
          <Column alignmentVertical='bottom'>
            {isBottomQuartile && !cardContent.isManager && (
              <Button
                type='tertiary'
                onClick={navigate}
                href={renderUrl(
                  `${urls.metrics}/${userID}${BottomQuartileRoutingInfo}#nav-bottom-quartile`
                )}
                data-csa-c-type='button'
                data-csa-c-slot-id='arc-card-view-bottom-quartile-performance-button-slot'
                data-csa-c-content-id={`arc-card-open-coaching-notes-for-${cardContent.userID}`}
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Associates'
                data-csa-c-tag3='Card'
                data-csa-c-tag4='Summary'
                data-csa-c-profile-id={`${cardContent.userID}`}
                data-csa-c-username={requesterUsername}
                data-csa-c-is-tm={isTm}
              >
                <FormattedMessage
                  id='cs_perfportal_bq_view_bottom_quartile_button'
                  defaultMessage='View Bottom Quartile Performance'
                />
              </Button>
            )}
            {!sharedProfileUser && (
              <Button
                type='tertiary'
                onClick={navigate}
                href={renderUrl(
                  `${urls.metrics}/${userID}?selectedCSA=${cardContent.userID}&selectedVertical=${currentVertical}`
                )}
                data-csa-c-type='button'
                data-csa-c-slot-id={`arc-card-${sideMenuSource}-summary-view-all-metrics-button-slot`}
                data-csa-c-content-id={`arc-card-${sideMenuSource}-summary-view-all-metrics-for-${cardContent.userID}`}
                data-csa-c-profile-id={`${cardContent.userID}`}
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Associates'
                data-csa-c-tag3='Card'
                data-csa-c-tag4='Summary'
                data-cy={`arc-card-${sideMenuSource}-summary-view-all-metrics-for-${cardContent.userID}`}
              >
                <FormattedMessage
                  id='cs_perfportal_arc_view_all_metrics_button'
                  defaultMessage='View All Metrics'
                />
              </Button>
            )}
            <Button
              type='tertiary'
              onClick={navigate}
              href={renderUrl(`${urls.coachingNotes}/${cardContent.userID}`)}
              data-csa-c-type='button'
              data-csa-c-slot-id={`arc-card-${sideMenuSource}-open-coaching-notes-button-slot`}
              data-csa-c-content-id={`arc-card-${sideMenuSource}-open-coaching-notes-for-${cardContent.userID}`}
              data-csa-c-profile-id={`${cardContent.userID}`}
              data-csa-c-tag1='Team'
              data-csa-c-tag2='Associates'
              data-csa-c-tag3='Card'
              data-csa-c-tag4='Summary'
              data-csa-c-username={requesterUsername}
              data-csa-c-is-tm={isTm}
            >
              <FormattedMessage
                id='cs_perfportal_arc_go_to_coaching_notes'
                defaultMessage='Go to Coaching Notes'
              />
            </Button>
          </Column>
        </>
      )}

      {cardContent && cardContent.kudos && (
        <>
          <Row wrap='down' spacingInset='none' spacing='none'>
            <Row width='100%' spacingInset='none none 300'>
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_kudos'
                  defaultMessage='Kudos'
                />
              </Text>
            </Row>
            <Column spacing='none' spacingInset='none' width={'100%'}>
              {cardContent.kudos !== undefined && (
                <Column spacing='none' spacingInset='none'>
                  {cardContent.kudos.labelAndCount.length === 0 &&
                    noDataText(ARCFilters.KUDOS)}
                  {cardContent.kudos.labelAndCount.map(kudo => (
                    <Row
                      widths={['60%', '40%']}
                      width={'100%'}
                      alignmentVertical='top'
                      spacingInset='none none 300'
                    >
                      <Row alignmentHorizontal='start'>
                        <Text type='b300' alignment='start'>
                          {kudo.category}
                        </Text>
                      </Row>
                      <Row alignmentHorizontal='end'>
                        <Text type='b300' alignment='end'>
                          <FormattedMessage
                            id='cs_perfportal_arc_opportunity_count'
                            defaultMessage='{categoryCount} Counts'
                            values={{ categoryCount: kudo.count }}
                          />
                        </Text>
                      </Row>
                    </Row>
                  ))}
                </Column>
              )}
            </Column>
          </Row>
          <Button
            type='tertiary'
            onClick={navigate}
            href={renderUrl(
              `${urls.coachingNotes}/${cardContent.userID}#nav-recommended`
            )}
            data-csa-c-type='button'
            data-csa-c-slot-id={`arc-card-${sideMenuSource}-kudos-view-all-recommendations-button-slot`}
            data-csa-c-content-id={`arc-card-${sideMenuSource}-kudos-view-all-recommendations-for-${cardContent.userID}`}
            data-csa-c-profile-id={`${cardContent.userID}`}
            data-csa-c-tag1='Team'
            data-csa-c-tag2='Associates'
            data-csa-c-tag3='Card'
            data-csa-c-tag4='Kudos'
            data-cy={`arc-card-${sideMenuSource}-kudos-view-all-recommendations-for-${cardContent.userID}`}
          >
            <FormattedMessage
              id='cs_perfportal_arc_go_to_recommendation_button'
              defaultMessage='View All Recommendations'
            />
          </Button>
        </>
      )}
    </Column>
  );
};

export default RichCardContent;
